import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowUpCircle, ChevronsDown } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { fetchEscalated, deEscalateReport, type EscalatedReport } from './escalationApi'

/**
 * A6.21 — Eskalatsiya navbati. Junior moderator qiyin/yuqori-xavf shikoyatni
 * yuqori tierга ko'targanida shu yerga tushadi (junior navbatlaridan chiqadi).
 * Senior ochib hal qiladi (A6.19 qaror) yoki normal navbatga qaytaradi.
 */

const S = {
  title: 'Eskalatsiya navbati',
  desc: 'Yuqori tierга ko’tarilgan shikoyatlar (senior/specialist/lead)',
  empty: 'Eskalatsiya qilingan shikoyat yo’q',
  level: 'Daraja',
  category: 'Toifa',
  target: 'Obyekt',
  escReason: 'Eskalatsiya sababi',
  by: 'Kim',
  at: 'Qachon',
  open: 'Ochish',
  deEscalate: 'Qaytarish',
  deEscalated: 'Normal navbatga qaytarildi',
  failed: 'Amal bajarilmadi',
}

const LEVEL_LABEL: Record<string, string> = { senior: 'Senior', specialist: 'Mutaxassis', lead: 'Lead' }
const LEVEL_TONE: Record<string, string> = {
  senior: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  specialist: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  lead: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
}

function fmtTime(ts?: number): string {
  if (!ts || !Number.isFinite(ts)) return '—'
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return '—'
  }
}

export function EscalatedQueue() {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [rows, setRows] = useState<EscalatedReport[] | null>(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    fetchEscalated(100)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const deEscalate = useCallback(
    async (id: number) => {
      try {
        await deEscalateReport(id)
        toast(S.deEscalated, { tone: 'success' })
        setRows((prev) => (prev ? prev.filter((r) => r.id !== id) : prev))
      } catch (e) {
        toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      }
    },
    [toast],
  )

  return (
    <>
      <PageHeader title={S.title} screenId="A6.21" description={S.desc} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {rows === null || loading ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-tertiary">
            <ArrowUpCircle size={24} className="mx-auto mb-2 opacity-60" />
            {S.empty}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-border-default text-left text-xs text-tertiary">
                  <th className="px-2 py-1.5 font-medium">{S.level}</th>
                  <th className="px-2 py-1.5 font-medium">{S.category}</th>
                  <th className="px-2 py-1.5 font-medium">{S.target}</th>
                  <th className="px-2 py-1.5 font-medium">{S.escReason}</th>
                  <th className="px-2 py-1.5 font-medium">{S.by}</th>
                  <th className="px-2 py-1.5 font-medium">{S.at}</th>
                  <th className="px-2 py-1.5 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="cursor-pointer border-b border-border-subtle align-top hover:bg-surface-2"
                    onClick={() => navigate(`/moderation/${r.id}`)}
                  >
                    <td className="px-2 py-1.5">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${LEVEL_TONE[r.escalation_level ?? ''] ?? 'bg-surface-2 text-secondary border-border-default'}`}>
                        {LEVEL_LABEL[r.escalation_level ?? ''] ?? r.escalation_level ?? '—'}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-secondary">{r.category}</td>
                    <td className="px-2 py-1.5 font-mono text-secondary">{r.target_type}#{r.target_id}</td>
                    <td className="max-w-xs truncate px-2 py-1.5 text-secondary" title={r.escalation_reason ?? ''}>{r.escalation_reason ?? '—'}</td>
                    <td className="px-2 py-1.5 text-tertiary">{r.escalated_by ?? '—'}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-tertiary">{fmtTime(r.escalated_at)}</td>
                    <td className="whitespace-nowrap px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        <Link
                          to={`/moderation/${r.id}`}
                          className="inline-flex items-center gap-1 rounded-md bg-accent px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-hover"
                        >
                          {S.open}
                        </Link>
                        <button
                          onClick={() => void deEscalate(r.id)}
                          className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-xs font-medium text-secondary hover:text-primary"
                        >
                          <ChevronsDown size={12} />
                          {S.deEscalate}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
